import React, { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Skeleton,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import StoreIcon from "@mui/icons-material/Store";
import type { Store } from "../../../../types/Store";
import { useStoreData } from "../../../../context/StoreDataContext";

interface CustomerStoreCardProps {
    store: Store | null;
    loading: boolean;
}

export const StoreDetailDialog = ({ store, open, onClose, loading = false }: { store: Store | null; open: boolean; onClose: () => void; loading?: boolean }) => {
    const { wayTypes } = useStoreData();

    const getWayTypeName = (wayTypeId: string | number | null | undefined) => {
        if (!wayTypeId) return "";
        const wt = wayTypes?.find(w => w.id.toString() === wayTypeId.toString());
        return wt ? wt.name : "";
    };

    const isClosed = store ? (!!store.CLOSING_DATE && new Date(store.CLOSING_DATE) <= new Date()) : false;
    const city = store ? (store.CITY || store.LOCAL_CITY_NAME || store.SHORT_CITY_NAME) : null;
    const storeHeading = store ? ([store.IDENTIFIER, city].filter(Boolean).join(" – ") || store.NAME) : null;

    const street3Line = store
        ? ([store.ADDRESS3_NUM, getWayTypeName(store.ADDRESS3_TYPE), store.ADDRESS3_NAME].filter(Boolean).join(" ") || store.ADDRESS3 || null)
        : null;
    const addrLines = store ? ([store.ADDRESS1, store.ADDRESS2, store.ADDRESS4].filter(Boolean) as string[]) : [];
    const cityLine = store ? ([store.ZIP_CODE, store.LOCAL_CITY_NAME || store.CITY].filter(Boolean).join(" ") || null) : null;
    const countryLine = store ? (store.LOCAL_COUNTRY_NAME || store.COUNTRY || null) : null;

    const phone = store ? (store.PHONE || store.PHONE_NUMBER) : null;
    const email = store ? (store.EMAIL || store.EMAIL_ADDRESS) : null;

    const transactionMode = store?.TRANSACTION_INTEGRATION_MODE;
    const caisseWebServices = transactionMode
        ? (transactionMode === "0" || transactionMode.toUpperCase() === "NON" || transactionMode.toUpperCase() === "NO" ? "NON" : transactionMode)
        : null;

    const Row = ({ icon, label, value }: { icon?: React.ReactNode; label: string; value?: string | null }) => {
        if (!value) return null;
        return (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
                {icon && <Box sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}>{icon}</Box>}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25, wordBreak: "break-word", overflowWrap: "anywhere" }}>{value}</Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 6, pb: 1 }}>
                <StoreIcon color="primary" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {loading || !storeHeading
                        ? <Skeleton variant="text" width="60%" sx={{ fontSize: "1.1rem" }} />
                        : <Typography variant="subtitle1" fontWeight={700} noWrap>{storeHeading}</Typography>
                    }
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.25 }}>
                        {loading
                            ? <Skeleton variant="rounded" width={50} height={20} />
                            : <>
                                <Chip
                                    label={isClosed ? "Fermé" : "Ouvert"}
                                    size="small"
                                    color={isClosed ? "error" : "success"}
                                    sx={{ fontSize: "0.7rem", height: 20 }}
                                    icon={isClosed
                                        ? <EventBusyIcon sx={{ fontSize: "14px !important" }} />
                                        : <EventAvailableIcon sx={{ fontSize: "14px !important" }} />
                                    }
                                />
                            </>
                        }
                    </Box>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                        <CircularProgress size={36} />
                    </Box>
                ) : !store ? null : (
                <Grid container spacing={2}>
                    {/* Informations légales */}
                    {(store.NAME || store.SHORT_CITY_NAME || store.RCS) && (
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 0.75 }}>
                                Informations légales
                            </Typography>
                            <Row label="Raison sociale" value={store.NAME} />
                            {store.SHORT_CITY_NAME && (
                                <Row label="Nom de magasin" value={store.SHORT_CITY_NAME} />
                            )}
                            {store.RCS && (
                                <Row label="RCS" value={store.RCS} />
                            )}
                        </Grid>
                    )}
                    {(store.NAME || store.SHORT_CITY_NAME || store.RCS) && (
                        <Grid size={{ xs: 12 }}>
                            <Divider />
                        </Grid>
                    )}
                    {/* Adresse */}
                    {(street3Line || addrLines.length > 0 || cityLine || countryLine) && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 0.75 }}>
                                Adresse
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                <LocationOnIcon sx={{ fontSize: 16, color: "text.secondary", mt: 0.25, flexShrink: 0 }} />
                                <Box>
                                    {street3Line && <Typography variant="body2">{street3Line}</Typography>}
                                    {addrLines.map((line, i) => (
                                        <Typography key={i} variant="body2">{line}</Typography>
                                    ))}
                                    {cityLine && <Typography variant="body2">{cityLine}</Typography>}
                                    {countryLine && <Typography variant="body2" color="text.secondary">{countryLine}</Typography>}
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {/* Contact */}
                    {(phone || email) && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 0.75 }}>
                                Contact
                            </Typography>
                            <Row icon={<PhoneIcon fontSize="small" />} label="Téléphone" value={phone} />
                            <Row icon={<EmailIcon fontSize="small" />} label="Email" value={email} />
                        </Grid>
                    )}

                    {/* Caisse en web services */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Row label="Caisse en web services" value={caisseWebServices} />
                    </Grid>

                    {/* Dates */}
                    {(store.OPENING_DATE || store.CLOSING_DATE) && (
                        <>
                            <Grid size={{ xs: 12 }}>
                                <Divider />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                {store.OPENING_DATE && (
                                    <Row
                                        icon={<EventAvailableIcon fontSize="small" />}
                                        label="Date d'ouverture"
                                        value={new Date(store.OPENING_DATE).toLocaleDateString("fr-FR")}
                                    />
                                )}
                                {store.CLOSING_DATE && (
                                    <Row
                                        icon={<EventBusyIcon fontSize="small" />}
                                        label="Date de fermeture"
                                        value={new Date(store.CLOSING_DATE).toLocaleDateString("fr-FR")}
                                    />
                                )}
                            </Grid>
                        </>
                    )}

                    {/* Horaires */}
                    {store.OPENING_HOURS && (
                        <>
                            <Grid size={{ xs: 12 }}>
                                <Divider />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 0.75 }}>
                                    Horaires
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{store.OPENING_HOURS}</Typography>
                            </Grid>
                        </>
                    )}

                </Grid>
                )}
            </DialogContent>
        </Dialog>
    );
};

const CustomerStoreCard = ({ store, loading }: CustomerStoreCardProps): React.ReactElement => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const { wayTypes } = useStoreData();

    const getWayTypeName = (wayTypeId: string | number | null | undefined) => {
        if (!wayTypeId) return "";
        const wayType = wayTypes?.find(wt => wt.id.toString() === wayTypeId.toString());
        return wayType ? wayType.name : "";
    };

    if (loading) {
        return (
            <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="text" width="60%" height={24} />
                    </Box>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="50%" />
                    <Skeleton variant="text" width="70%" />
                    <Skeleton variant="text" width="40%" />
                </CardContent>
            </Card>
        );
    }

    if (!store) {
        return (
            <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 4 }}>
                    <StoreIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        Aucune information magasin
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const parseDMY = (s: string) => { const [d, m, y] = s.split('/').map(Number); return new Date(y, m - 1, d); };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const isClosed = !!store.UNSUBSCRIBE_DATE && parseDMY(store.UNSUBSCRIBE_DATE) <= today;
    const city = store.CITY || store.LOCAL_CITY_NAME || store.SHORT_CITY_NAME;
    const storeHeading = [store.IDENTIFIER, city].filter(Boolean).join(" – ");
    const street3Line = [store.ADDRESS3_NUM, getWayTypeName(store.ADDRESS3_TYPE), store.ADDRESS3_NAME].filter(Boolean).join(" ")
        || store.ADDRESS3 || null;
    const otherAddrLine = [store.ADDRESS1, store.ADDRESS2, store.ADDRESS4].filter(Boolean).join(" ") || null;
    const cityLine = [store.ZIP_CODE, store.LOCAL_CITY_NAME || store.CITY].filter(Boolean).join(" ") || null;
    const countryLine = store.LOCAL_COUNTRY_NAME || store.COUNTRY || null;
    const hasAddress = street3Line || otherAddrLine || cityLine || countryLine;

    const phone = store.PHONE || store.PHONE_NUMBER;
    const email = store.EMAIL || store.EMAIL_ADDRESS;

    return (
        <>
            <Card
                variant="outlined"
                sx={{ borderRadius: 2, overflow: "hidden" }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 2, py: 1, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "divider" }}>
                    <Box sx={{ color: "primary.main", display: "flex", opacity: 0.8, "& svg": { fontSize: 15 } }}>
                        <StoreIcon />
                    </Box>
                    <Typography variant="caption" fontWeight={700} sx={{ color: "text.secondary", letterSpacing: "0.6px", textTransform: "uppercase", fontSize: "0.7rem" }}>
                        Magasin
                    </Typography>
                </Box>
                <CardContent sx={{ p: 2, pt: 1.5, "&:last-child": { pb: 2 } }}>
                    <Box sx={{ mb: 1.25 }}>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setDialogOpen(true)}
                            title="Voir les informations du magasin"
                            sx={{
                                display: "block",
                                p: 0, border: 0, bgcolor: "transparent",
                                font: "inherit", fontWeight: 600,
                                lineHeight: 1.4, textAlign: "left",
                                cursor: "pointer", maxWidth: "100%",
                                color: "inherit",
                                "&:hover .store-link": { textDecoration: "underline" },
                            }}
                        >
                            <Typography className="store-link" variant="subtitle2" fontWeight={700} color="primary.main" noWrap>
                                {storeHeading || store.NAME}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
                        <Chip
                            label={isClosed ? "Fermé" : "Ouvert"}
                            size="small"
                            color={isClosed ? "error" : "success"}
                            sx={{ fontSize: "0.7rem", height: 20 }}
                            icon={isClosed
                                ? <EventBusyIcon sx={{ fontSize: "14px !important" }} />
                                : <EventAvailableIcon sx={{ fontSize: "14px !important" }} />
                            }
                        />
                    </Box>

                    {hasAddress && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.75 }}>
                            <LocationOnIcon sx={{ fontSize: 14, color: "text.secondary", mt: 0.25, flexShrink: 0 }} />
                            <Box>
                                {street3Line && <Typography variant="caption" color="text.secondary" display="block">{street3Line}</Typography>}
                                {otherAddrLine && <Typography variant="caption" color="text.secondary" display="block">{otherAddrLine}</Typography>}
                                {cityLine && <Typography variant="caption" color="text.secondary" display="block">{cityLine}</Typography>}
                                {countryLine && <Typography variant="caption" color="text.secondary" display="block">{countryLine}</Typography>}
                            </Box>
                        </Box>
                    )}

                    {(phone || email) && <Divider sx={{ my: 1 }} />}

                    {phone && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">{phone}</Typography>
                        </Box>
                    )}
                    {email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <EmailIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary" noWrap>{email}</Typography>
                        </Box>
                    )}

                    {store.OPENING_DATE && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1, fontSize: "0.68rem" }}>
                            Ouvert depuis le {new Date(store.OPENING_DATE).toLocaleDateString("fr-FR")}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {dialogOpen && (
                <StoreDetailDialog
                    store={store}
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                />
            )}
        </>
    );
};

export default CustomerStoreCard;
